import React, { useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  Edit,
  EditProps,
  Link,
  SaveButton,
  SimpleForm,
  Toolbar,
  TopToolbar,
  useNotify,
  useRecordContext, useRefresh, useSidebarState,
} from 'react-admin';
import { Col, Row } from 'antd';
import { Button } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { CreditRisk, CreditStatus } from '@types';
import { creditRiskHttp } from '@network';
import { axiosErrorToString, rbac } from '@utils';
import { RiskChat } from '@pages/credit-risk/RiskChat';
import { Terms } from '@pages/credit-risk/terms';
import { RiskTimeTracker } from '@pages/credit-risk/RiskTimeTracker';
import { RiskCreditReport } from '@pages/credit-risk/RiskCreditReport';
import { RiskProgressScore } from '@pages/credit-risk/RiskProgressScore';
import { BlueOutlineButton, Forbidden, InitialPage, Loader } from '@components';
import { useEditProps, useQuery, useRbacEdit, useRbacView, useRecord } from '@hooks';
import { CreditSurveyProgress } from '@pages/credit/CreditInitial/CreditSurveyProgress';
import { ExportRisk } from '@pages/credit-risk/export';
import { LegacyAnalysis } from '@pages/credit-risk/legacy-analysis';
import { Analysis } from '@pages/credit-risk/analysis';
import { RiskTapeFile } from '@pages/credit-risk/RiskTapeFile';
import { CreditSurveyView } from '@pages/credit/CreditEdit/CreditSurveyView';
import { RiskInitial } from '@pages/credit-risk/RiskInitial';
import { OldAnalysis } from '@pages/credit-risk/old-analysis';
import { RiskAnalysisReport } from '@pages/credit-risk/RiskAnalysisReport';

const Title = () => {
  const record = useRecordContext();
  return (<span>CreditRisk # {record?.id}</span>);
};

export const CreditRiskEdit = (props: EditProps) => {
  const risk: CreditRisk | undefined = useRecord<CreditRisk>(props);
  const refresh = useRefresh();
  const query = useQuery();
  const editProps = useEditProps();
  const notify = useNotify();
  const rbacView = useRbacView();
  const rbacEdit = useRbacEdit();
  const [isReportOpened, setReportOpened] = useState(false);
  const [isReportPrepared] = useState(true);
  const reportNodeRef = useRef(null);
  const cls = useStyles();

  const isSuperAdmin = rbac.isSuperadmin();
  const riskManagerReadonly = rbac.hasRiskManagerReadonlyPerm();
  const hasSendToAnalysisRole = rbac.hasSendToAnalysisRole();
  const readonly = riskManagerReadonly && !isSuperAdmin;

  const backTo = risk ? `/credit/${risk.creditId}?report` : '#';
  const toRiskAnalysis = risk ? `/credit-risk/${risk.id}?analysis` : '#';
  const toRisk = risk ? `/credit-risk/${risk.id}` : '#';
  const showAnalysis = query.get('analysis') === '';

  if (!rbacView && !riskManagerReadonly && !hasSendToAnalysisRole) {
    return <Forbidden />;
  }

  const completeAnalysis = async () => {
    if (readonly) return;
    try {
      await creditRiskHttp.complete(risk.id);
      refresh();
      setReportOpened(true);
    } catch (e) {
      notify(axiosErrorToString(e));
    }
  };

  const showAnalysisBtn = risk?.credit.status === CreditStatus.BANK_REVISION
    || risk?.credit.user.responsibleAdminId === 42;

  const EditActions = () => {
    return rbacEdit ? (
      <Toolbar>
        <div className={cls.toolbar}>
          {showAnalysisBtn ? (
            <Link to={toRiskAnalysis}>
              <Button variant="contained" color="secondary">Start Analysis</Button>
            </Link>
          ) : null}

          <div className={cls.generalActions}>
            <Link to={backTo}>
              <BlueOutlineButton className={cls.actionBack}>Go back</BlueOutlineButton>
            </Link>
            <SaveButton label="Save" type="button" disabled={readonly} />
          </div>
        </div>
      </Toolbar>
    ) : null;
  };

  const AnalysisActions = () => {
    const [open] = useSidebarState();

    return (
      <>
        <TopToolbar className={clsx(cls.actions, open && cls.actionsSmall)}>
          <div className={cls.actionsRow}>
            <div className={clsx(cls.actionsCol, cls.actionsColFirst)}>
              <SaveButton
                icon={<></>}
                label="Only Save Changes"
                type="button"
                className={clsx(cls.action, cls.actionSave)}
                variant="outlined"
                disabled={readonly}
                alwaysEnable={readonly ? false : true}
              />
            </div>
            <div className={clsx(cls.actionsCol, cls.actionsColLast)}>
              <Link to={toRisk}>
                <BlueOutlineButton className={clsx(cls.action, cls.actionBack)}>
                  Go back
                </BlueOutlineButton>
              </Link>
              <SaveButton
                className={clsx(cls.action, cls.actionComplete)}
                variant="contained"
                color="secondary"
                disabled={readonly}
                alwaysEnable
                label="Complete and Save"
                mutationOptions={{
                  onSuccess: () => void completeAnalysis(),
                }}
                type="button"
              />
            </div>
          </div>
        </TopToolbar>
        <ExportRisk
          creditId={risk.creditId}
          isOpened={isReportOpened}
          setOpened={setReportOpened}
        />
      </>
    );
  };

  if (showAnalysis) {
    return risk ? (
      <Edit {...props} title={<Title />} {...editProps}
        className={clsx(cls.page, 'details-page-wrapper')}>
        <SimpleForm toolbar={rbacEdit ? <AnalysisActions /> : false} className={cls.form}>
          {risk.isLegacy ? (
            <LegacyAnalysis risk={risk} />
          ) : (
            <>
              {risk.isOld ? (
                <OldAnalysis risk={risk} readonly={readonly} />
              ) : (
                <Analysis risk={risk} readonly={readonly} />
              )}
            </>
          )}
        </SimpleForm>
      </Edit>
    ) : <Loader />;
  }

  const isFixed = risk?.isFixed || !!readonly;

  return (
    <InitialPage
      title="Risk Summary"
      backTo="/credit-risk"
      backText="Back to all records"
    >
      <Edit {...props} title={<Title />} {...editProps}>
        <SimpleForm toolbar={<EditActions />}>
          {risk && (
            <Row gutter={[20, 20]} className={cls.columns}>
              <Col sm={24} lg={12}>
                <RiskInitial risk={risk} isFixed={isFixed} />
                {risk.isOld ? (
                  <RiskCreditReport
                    risk={risk}
                    isPrepared={isReportPrepared}
                    reportNodeRef={reportNodeRef}
                  />
                ) : (
                  <RiskAnalysisReport risk={risk} />
                )}
                <RiskChat risk={risk} readonly={readonly} />
              </Col>
              <Col sm={24} lg={12}>
                <RiskProgressScore risk={risk} readonly={readonly} />
                <Terms risk={risk} readonly={readonly} />
                <Row gutter={[20, 20]}>
                  <Col sm={24} xl={12} className={cls.survey}>
                    <CreditSurveyProgress
                      credit={risk.credit}
                      link={`/credit/${risk.creditId}/?survey-view`}
                    >
                      <CreditSurveyView credit={risk.credit} hide />
                    </CreditSurveyProgress>
                  </Col>
                  <Col sm={24} xl={12} className={cls.analysis}>
                    <RiskTimeTracker risk={risk} />
                    <RiskTapeFile risk={risk} forwardRef={reportNodeRef} />
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </SimpleForm>
      </Edit>
    </InitialPage>
  );
};

const useStyles = makeStyles({
  toolbar: {
    position: 'fixed',
    left: 250,
    bottom: 0,
    zIndex: 1000,
    width: 'calc(100vw - 250px) !important',
    backgroundColor: 'var(--foreground-color) !important',
    padding: '0 66px 0 4px !important',
    paddingRight: 'calc(66px + env(scrollbar-width, 0px))',
    borderTop: '1px solid var(--logo-color)',
    display: 'flex !important',
    alignItems: 'center !important',
    transform: 'translateZ(0)',
    willChange: 'transform',
    backfaceVisibility: 'hidden',
    minHeight: 64,

    '& button[aria-label="Save"]': {
      marginLeft: '-24px !important',
    },
  },
  columns: {
    width: '100%',
  },
  page: {
    padding: '30px 44px 70px',
  },
  form: {
    padding: 0,
  },
  actions: {
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 25,
    position: 'fixed',
    bottom: 0,
    right: 0,
    padding: '16px 25px 11px',
    backgroundColor: 'var(--foreground-color)',
    boxShadow: '0px 4px 4px 0px #00000040 inset',
    width: 'calc(100% - 70px)',
    transition: 'width 225ms cubic-bezier(0, 0, 0.2, 1)',
  },
  actionsSmall: {
    width: 'calc(100% - 250px)',
    transition: 'width 225ms cubic-bezier(0, 0, 0.2, 1)',
  },
  actionsRow: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionsCol: {},
  actionsColFirst: {},
  actionsColLast: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 32,
  },
  actionIcon: {
    marginRight: 10,
    fontSize: 18,
    color: 'white',
  },
  action: {
    width: '200px !important',
    height: '38px !important',
    borderRadius: '8px!important',
    fontWeight: '400 !important',
    lineHeight: 'normal',
  },
  actionSave: {},
  actionTopSave: {
    position: 'absolute',
    top: 40,
    right: 40,
    color: '#ffffff !important',
    background: 'var(--primary-color) !important',
    minWidth: 160,
    height: 38,
    textTransform: 'none',
    borderRadius: 8,
  },
  actionComplete: {
    color: 'white !important',
    backgroundColor: 'var(--primary-color) !important',
  },
  actionBack: {
    color: 'var(--error-color) !important',
    border: '1px solid var(--error-color) !important',
  },
  generalActions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: 60,
  },
  analysis: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    minHeight: 300,
    justifyContent: 'space-between',
  },
  survey: {
    minHeight: 280,
  },
});
